import React from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  ExportEntityType,
  ProductEntityRole,
  ProductEntityLink,
} from 'api/ExportHubAPI';

/** Entity + product-link presets (spec §4.7). */

export const ENTITY_TYPE_OPTIONS: ExportEntityType[] = [
  'Customer',
  'Consignee',
  'End User',
  'Distributor',
  'Manufacturer',
];

export const PRODUCT_ENTITY_ROLE_OPTIONS: ProductEntityRole[] = [
  'Customer',
  'Consignee',
  'End User',
  'Distributor',
  'Manufacturer',
];

// Roles whose linked entity country contributes to effective destinations.
export const DESTINATION_ROLES: ProductEntityRole[] = [
  'Customer',
  'Consignee',
  'End User',
];

const ROLE_VARIANT: Record<ProductEntityRole, string> = {
  Customer: 'info',
  Consignee: 'info',
  'End User': 'warning',
  Distributor: 'muted',
  Manufacturer: 'muted',
};

export const RoleChip: React.FC<{ value: ProductEntityRole }> = ({ value }) => (
  <span className={`eh-pill eh-pill-${ROLE_VARIANT[value] || 'muted'}`}>
    {value}
  </span>
);

export const RiskFlags: React.FC<{ flags: string[] }> = ({ flags }) => {
  if (!flags || flags.length === 0) return null;
  return (
    <span className='eh-risk-flags'>
      <WarningAmberIcon style={{ fontSize: 13 }} />
      {flags.map((f) => (
        <span key={f} className='eh-risk-flag'>
          {f}
        </span>
      ))}
    </span>
  );
};

export interface EffectiveDestination {
  country: string;
  sources: string[];
}

/**
 * A product's effective destinations (spec §4.7): the union of linked
 * Customer/Consignee/End-User entity countries and the manually entered
 * intended destinations, each tagged with where it came from. Merged
 * case-insensitively so the same country isn't double-listed.
 */
export const computeEffectiveDestinations = (
  intended: string[],
  entities: ProductEntityLink[],
): EffectiveDestination[] => {
  const byKey = new Map<string, EffectiveDestination>();
  const add = (countryRaw: string | null, source: string): void => {
    const country = (countryRaw || '').trim();
    if (!country) return;
    const key = country.toLowerCase();
    const existing = byKey.get(key);
    if (existing) {
      if (!existing.sources.includes(source)) existing.sources.push(source);
    } else {
      byKey.set(key, { country: country, sources: [source] });
    }
  };

  (intended || []).forEach((c) => add(c, 'Intended destination'));
  (entities || [])
    .filter((e) => DESTINATION_ROLES.includes(e.role))
    .forEach((e) => add(e.country, `${e.role}: ${e.name}`));

  return Array.from(byKey.values());
};

/** Unique effective-destination country names (for coverage checks). */
export const effectiveDestinationCountries = (
  intended: string[],
  entities: ProductEntityLink[],
): string[] => computeEffectiveDestinations(intended, entities).map((d) => d.country);
